import { env } from '@/shell/env';
import { adminParentDomain } from '@/shell/org';

/**
 * Domain these cookies are scoped to. It MUST be the parent shared by the bare
 * admin host and every org subdomain — a host-only cookie breaks every
 * cross-subdomain hop: a session written on an org subdomain is invisible on
 * the bare domain, so the supervisor lobby finds no session and bounces to
 * /login with no way to reach any org.
 *
 * The configured callback origin is authoritative where it exists. Where it
 * does not, derive the same parent from the current host rather than falling
 * back to host-only, which fails silently and only off the deployed origin.
 * Host-only is correct for a host with no `admin.`/`app.` label (plain
 * `localhost`), which has no sibling subdomains.
 */
function getCookieDomain(): string | undefined {
  if (env.ssoCallbackOrigin) {
    try {
      return `.${new URL(env.ssoCallbackOrigin).hostname}`;
    } catch {
      // Malformed override — fall through to the host-derived parent.
    }
  }
  const parent = adminParentDomain();
  return parent ? `.${parent}` : undefined;
}

export function setCookie(name: string, value: string, maxAge: number): void {
  const domain = getCookieDomain();
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    'path=/',
    `max-age=${maxAge}`,
    'samesite=lax',
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('secure');
  document.cookie = parts.join('; ');
}

export function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function deleteCookie(name: string): void {
  // Expire both the domain-scoped cookie and any host-only straggler with
  // the same name (e.g. planted by an older bundle or a dev tool) — a
  // host-only twin would otherwise shadow or survive the deletion.
  setCookie(name, '', 0);
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${name}=; path=/; max-age=0; samesite=lax${secure}`;
}

export function setSsoState(
  verifier: string,
  state: string,
  next: string,
  origin: string,
): void {
  const maxAge = 300;
  setCookie('sso_verifier', verifier, maxAge);
  setCookie('sso_state', state, maxAge);
  setCookie('sso_next', next, maxAge);
  setCookie('sso_origin', origin, maxAge);
}

export function getSsoState(): {
  verifier: string | null;
  state: string | null;
  next: string | null;
  origin: string | null;
} {
  return {
    verifier: getCookie('sso_verifier'),
    state: getCookie('sso_state'),
    next: getCookie('sso_next'),
    origin: getCookie('sso_origin'),
  };
}

export function clearSsoState(): void {
  deleteCookie('sso_verifier');
  deleteCookie('sso_state');
  deleteCookie('sso_next');
  deleteCookie('sso_origin');
}

// setSsoJwt/consumeSsoJwt (the transient cross-subdomain JWT transfer) are
// gone: the session itself now lives in a parent-domain cookie
// (shell/auth/sessionCookies.ts), so there is nothing to hand off.
